// Copyright (c) 2019-2024 Cigent Technology Inc. All Rights Reserved.
import { Component, OnInit, Input, Output, EventEmitter, ViewChild, ElementRef, QueryList, ViewChildren,
  HostListener } from '@angular/core';
import { IPolicyListItem } from 'src/types/d3ePolicy';
import { MatDialog } from '@angular/material/dialog';
import { AlertDialogComponent } from 'src/app/dialogs/alert/alert.component';
import { NotificationService } from 'src/services/notification-service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { D3eService } from 'src/services/d3e-service';
import { SelectedOrgMethod } from 'src/shared/selectedOrg';
import { TranslateService } from '@ngx-translate/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {createDiscardDialog} from '../../../guards/page.guard';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-policy-list',
    templateUrl: './policy-list.component.html',
    styleUrls: ['./policy-list.component.scss'],
    standalone: false
})
export class PolicyListComponent implements OnInit {

  @Input() policies: IPolicyListItem[];
  @Input() loadedPolicy: number = null;
  @Input() isDirty: boolean;
  @Input() selectedOrgId: string;

  @Output() policyLoad: EventEmitter<number> = new EventEmitter<number>();
  @Output() policyDelete: EventEmitter<number> = new EventEmitter<number>();
  @Output() policyAdd: EventEmitter<string> = new EventEmitter<string>();
  @Output() policyClone: EventEmitter<string> = new EventEmitter<string>();
  @Output() policyRename: EventEmitter<{ id: number; newName: string; }> = new EventEmitter();
  @Output() priorityChange: EventEmitter<number[]> = new EventEmitter();
  @Output() policyCanceled: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('renameInput') renameInput: ElementRef;
  @ViewChild('newInput') newInput: ElementRef;
  @ViewChildren('policyListItems', {read: ElementRef}) policyListItems: QueryList<ElementRef>;

  newName = '';
  newMode = false;

  renameMode = false;
  renameTo = '';

  reorderMode = false;
  ariaReorderMode = false;
  policiesOrig: IPolicyListItem[];

  announcement = '';
  fAdd: string;
  fReorder: string;
  fDeleteTip: string;
  fNewTip: string;
  fCancel: string;
  fApply: string;
  fReorderButton: string;
  fNoteA: string;
  fNoteB: string;
  fNoteC: string;
  fDiscardTitle: string;
  fStay: string;
  fDiscard: string;
  fErrorEmptyName: string;
  nameError: string = null;

  constructor(
    public dialog: MatDialog,
    private d3eService: D3eService,
    private notification: NotificationService,
    private translate: TranslateService
  ) {
    this.translate.get(['PL_ADD', 'PL_REORDER', 'PL_DELETE_TIP', 'PL_NEW_TIP', 'CANCEL', 'PL_APPLY',
      'PL_REORDERB', 'PL_NOTEA', 'PL_NOTEB', 'PL_NOTEC', 'DISCARD_CHANGES_TITLE', 'STAY', 'DISCARD',
      'PL_ERROR_EMPTY_NAME']).subscribe(vals => {
        this.fAdd = vals['PL_ADD'];
        this.fReorder = vals['PL_REORDER'];
        this.fDeleteTip = vals['PL_DELETE_TIP'];
        this.fNewTip = vals['PL_NEW_TIP'];
        this.fCancel = vals['CANCEL'];
        this.fApply = vals['PL_APPLY'];
        this.fReorderButton = vals['PL_REORDERB'];
        this.fNoteA = vals['PL_NOTEA'];
        this.fNoteB = vals['PL_NOTEB'];
        this.fNoteC = vals['PL_NOTEC'];
        this.fDiscardTitle = vals['DISCARD_CHANGES_TITLE'];
        this.fStay = vals['STAY'];
        this.fDiscard = vals['DISCARD'];
        this.fErrorEmptyName = vals['PL_ERROR_EMPTY_NAME'];
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  ngOnInit() {
  }

  loadPolicy(policy: IPolicyListItem) {
    if (this.loadedPolicy === policy.id) {
      this.renameTo = policy.name;
      if (!this.renameMode) {
        this.renameMode = true;
        this.newMode = false;
        setTimeout(() => {
          this.renameInput.nativeElement.focus();
          this.renameInput.nativeElement.select();
        }, 0);
      }
    } else {
      this.renameMode = false;
      if (this.isDirty) {
        createDiscardDialog(this.dialog, this.translate).subscribe(result => {
          if (result) {
            this.policyLoad.emit(policy.id);
          }
        });
      } else {
        this.policyLoad.emit(policy.id);
      }
    }
  }

  deletePolicy(id: number, event) {
    const selectedPolicy = this.policies.find(p => p.id == this.loadedPolicy);
    const selectedIndex = this.policies.indexOf(selectedPolicy);
    if (selectedIndex == this.policies.length - 1) {
      this.notification.showError(this.translate.instant('CANNOT_DELETE_DEFAULT_POLICY'));
      return;
    }
    const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);
    this.d3eService.getDeviceSetsForPolicy(id, selectedOrg)
      .pipe(untilDestroyed(this))
      .subscribe(deviceSets => {
        const vals = this.translate.instant(['DELETE_POLICY_TITLE_WARNING', 'ALERT_OK', 'YES', 'NO',
          'DELETE_POLICY_TITLE'], {
            deviceSets: deviceSets.join(', ')
        });
        if (deviceSets.length > 0) {
          const dialogRef = this.dialog.open(AlertDialogComponent, {
            width: '450px',
            data: {
              title: '',
              content: vals['DELETE_POLICY_TITLE_WARNING'],
              acceptBtnName: vals['ALERT_OK'],
              showRejectBtn: false,
            },
          });
        } else {
          const policyName = this.policies.find(p => p.id == this.loadedPolicy).name;
          const dialogRef = this.dialog.open(AlertDialogComponent, {
            width: '450px',
            data: {
              title: this.translate.instant('DELETE_POLICY_TITLE'),
              content: this.translate.instant('DELETE_POLICY_TEXT', {'name': policyName}),
              rejectBtnName: vals['NO'],
              acceptBtnName: vals['YES'],
              showRejectBtn: true,
            },
          });
          dialogRef.afterClosed().subscribe(result => {
            if (result) {
              this.policyDelete.emit(id);
            }
          });
        }
    });
    event.stopPropagation();
  }

  clonePolicy(id: number, event: Event) {
    if (this.isDirty) {
      this.notification.showError(this.translate.instant('CLONE_POLICY_SAVE_FIRST'));
    } else {
      const oldName = this.policies.find(p => p.id == this.loadedPolicy).name;
      let newNameAlreadyExists = false;
      let newName = "";
      let num = 0;
      do {
        num++;
        newName = this.translate.instant('CLONED_POLICY_NAME', {num: num, name: oldName});
        newNameAlreadyExists = !!this.policies.find(p => p.name == newName);
      } while (newNameAlreadyExists);
      this.policyClone.emit(newName);
      event.stopPropagation();
    }
  }

  renamePolicy(event) {
    if (this.renameTo.length > 0) {
      this.policyRename.emit({id: this.loadedPolicy, newName: this.renameTo.trim()});
      this.renameMode = false;
    } else {
      this.notification.showError(this.fErrorEmptyName);
    }
    if (event) {
      event.stopPropagation();
    }
  }

  addPolicy() {
    if (this.newName.length > 0) {
      this.policyAdd.emit(this.newName.trim());
      this.newMode = false;
    } else {
      this.notification.showError(this.fErrorEmptyName);
    }
  }

  setNewPolicyName() {
    let i = 1;
    let name: string;
    const names = this.policies.map(e => e.name);
    // eslint-disable-next-line no-constant-condition
    while (true) {
      name = 'New Policy ' + i;
      if (names.indexOf(name) === -1) {
        this.newName = name;
        break;
      }
      i++;
    }
  }

  enableNewMode() {
    if (this.isDirty) {
      const vals = this.translate.instant(['DISCARD_CHANGES_TITLE', 'STAY', 'DISCARD']);
      const dialogRef = this.dialog.open(AlertDialogComponent, {
        width: '450px',
        data: {
          title: '',
          content: vals['DISCARD_CHANGES_TITLE'],
          rejectBtnName: vals['STAY'],
          acceptBtnName: vals['DISCARD'],
          showRejectBtn: true,
        },
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.policyCanceled.emit();
          this.doEnableNewMode();
        }
      });
    } else {
      this.doEnableNewMode();
    }
  }

  doEnableNewMode() {
    this.newMode = true;
    this.renameMode = false;
    this.setNewPolicyName();
    setTimeout(() => {
      this.newInput.nativeElement.focus();
      this.newInput.nativeElement.select();
    }, 0);
  }

  disableNewMode() {
    this.newName = '';
    this.newMode = false;
  }

  disableRenameMode(event) {
    event.stopPropagation();
    this.renameMode = false;
    this.renameTo = '';
  }

  enableReorderMode() {
    this.policiesOrig = Object.assign([], this.policies);
    this.reorderMode = true;
    setTimeout(() => {
      this.policyListItems.first.nativeElement.focus();
    }, 0);
  }

  cancelReorderMode() {
    this.policies = this.policiesOrig;
    this.reorderMode = false;
    this.ariaReorderMode = false;
  }

  applyReorderMode() {
    this.priorityChange.emit(this.policies.map(e => e.id));
    this.reorderMode = false;
    this.ariaReorderMode = false;
  }

  drop(event: CdkDragDrop<IPolicyListItem[]>) {
    moveItemInArray(this.policies, event.previousIndex, event.currentIndex);
  }

  updateLiveText(announcement: string) {
    this.announcement = '';
    this.announcement = announcement;
   }

  @HostListener('keydown', ['$event'])
  clickEvent(event) {
    const code = event.code.toLowerCase();
    const fromIndex = +event.target.dataset.targetIndex;
    const currentPolicyName = event.target.dataset.targetName;
    let toIndex = fromIndex;
    const totalItems = this.policies.length;
    const lastIndex = totalItems - 1;
    let announcement = '';
    this.updateLiveText(announcement);

    if (!this.reorderMode || code === 'tab') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (code === 'space') {
      if (this.ariaReorderMode) {
        this.applyReorderMode();
        announcement = 'Policy reorder saved.';
        this.updateLiveText(announcement);
      } else {
        this.ariaReorderMode = true;
        announcement = `${currentPolicyName} policy grabbed.
                        Press up and down arrow keys to change position, spacebar to drop, Escape to cancel.`;
        this.updateLiveText(announcement);
      }
    }

    if (code === 'escape') {
      this.cancelReorderMode();
      announcement = 'Policy reorder cancelled.';
      this.updateLiveText(announcement);
    }

    if (this.ariaReorderMode) {
      if (code === 'arrowdown' || code === 'arrowup') {
        toIndex = code === 'arrowdown' ? toIndex + 1 : toIndex - 1;
        if (toIndex >= 0 && toIndex <= lastIndex) {
          moveItemInArray(this.policies, fromIndex, toIndex);
          announcement = `Policy ${currentPolicyName} moved. Current position ${toIndex + 1} of ${this.policiesOrig.length}.`;
          this.updateLiveText(announcement);
        }
      }
    }
  }

  newNameChange() {
     if ( this.newName === '' ) {
       this.nameError = 'policy-list.error.no-name';
     } else {
       this.nameError = null;
       this.policies.forEach( policy => {
         if ( policy.name === this.newName ) {
           this.nameError = 'policy-list.error.duplicate-name';
         }
       });
     }
  }
}
